'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function HamilelikteDolgu() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/hamilelikte-dolgu-bilimsel-veriler-riskler-ve-uzman-yaklasimi';
  const pageTitle = 'Hamilelikte Dolgu: Bilimsel Veriler, Riskler ve Uzman Yaklaşımı';
  const pageDescription = 'Hamilelikte dolgu yaptırmak güvenli mi? Hyaluronik asit dolguların gebelikteki etkileri, bilimsel çalışmalar, riskler ve uzman önerileri.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-pink-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Hamilelikte Dolgu', href: '/tr/blog/hamilelikte-dolgu-bilimsel-veriler-riskler-ve-uzman-yaklasimi' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Hamilelikte dolgu uygulaması ve güvenlik"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-rose-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Hamilelik & Güvenlik
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Hamilelikte Dolgu: Bilimsel Veriler, Riskler ve Uzman Yaklaşımı
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>17 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>9 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Hamilelik dönemi, ciltte ve vücutta yaşanan en yoğun hormonal değişim dönemidir.
                Bu süreçte vücudun su tutma dengesi, damar geçirgenliği ve bağışıklık sistemi tamamen farklı çalışır.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bazı anne adaylarında yüzde hacim kaybı, gözaltı çökmesi veya dudak incelmesi gözlemlenir ve "küçük bir dolgu yaptırsam yeter" düşüncesi doğar.
              </p>

              <div className="bg-rose-50 border-l-4 border-rose-400 p-4 mb-6">
                <p className="text-rose-800 font-medium">
                  💡 Ancak estetik tıp açısından hamilelikte dolgu uygulamaları önerilmemektedir.
                  Bunun nedeni, hem bilimsel verilerin yetersizliği hem de vücudun hormonal olarak öngörülemez tepkiler verebilmesidir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Dolgu Maddeleri ve Etki Mekanizması
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Günümüzde en yaygın kullanılan dolgular hyaluronik asit (HA) temellidir.
                Bu madde insan dokularında doğal olarak bulunur ve su tutma kapasitesi sayesinde ciltte hacim ve nem kazandırır.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 mb-4 font-medium">
                  Dolgu maddeleri genellikle:
                </p>
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>dudak, burun, elmacık kemiği, çene hattı ve göz altına uygulanır,</li>
                  <li>vücut tarafından zamanla emilir.</li>
                </ul>
                <p className="text-blue-800 mt-4 font-medium">
                  Fakat gebelik döneminde;
                </p>
                <ul className="list-disc list-inside space-y-2 text-blue-800 mt-2">
                  <li>dolaşım artışı,</li>
                  <li>damar geçirgenliği,</li>
                  <li>hormonel ödem gibi faktörler, dolgunun davranışını öngörülemez hale getirir.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Bilimsel Durum: Dolguların Gebelikte Güvenliği
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Bugüne kadar hiçbir dolgu markası (Juvederm®, Restylane®, Teosyal®, Belotero® vb.)
                hamilelikte güvenli kullanım için FDA veya CE onayı almamıştır.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Yapılan az sayıdaki vakada dolgu sonrası ödem, asimetri, lokal iltihap gibi geçici reaksiyonlar bildirilmiştir.
                Ancak uzun dönem etkiler bilinmemektedir.
              </p>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <p className="text-yellow-800 font-medium">
                  FDA Dolgu Güvenlik Raporu (2024):
                </p>
                <p className="text-yellow-800 italic">
                  "Gebelikte dermal dolgu kullanımına dair yeterli veri yoktur; kozmetik amaçlı enjeksiyonlar ertelenmelidir."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Dolgu Maddelerinin Gebelikteki Olası Etkileri
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-rose-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etki Alanı</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Bağışıklık sistemi</td>
                      <td className="border border-gray-300 px-4 py-3">Gebelikte bağışıklık yanıtı değiştiği için alerjik reaksiyon riski artar.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Doku ödemi</td>
                      <td className="border border-gray-300 px-4 py-3">Hormonlar (özellikle progesteron) nedeniyle su tutulumu artar; dolgu dengesiz dağılabilir.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Damar geçirgenliği</td>
                      <td className="border border-gray-300 px-4 py-3">Artan kan hacmi dolgunun istenenden fazla yayılmasına neden olabilir.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Enfeksiyon riski</td>
                      <td className="border border-gray-300 px-4 py-3">Cilt bariyeri zayıflar, iyileşme süresi uzar.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-800 font-medium">
                  Dolayısıyla "zararsız içerik" olarak bilinen hyaluronik asit bile bu dönemde öngörülemez şekilde davranabilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Hamilelikte Dolgu Yapılırsa Ne Olabilir?
              </h2>
              
              <div className="bg-orange-50 rounded-xl p-6 mb-6">
                <p className="text-orange-800 mb-4 font-medium">
                  Hamilelikte yapılan dolgu işlemleri genellikle:
                </p>
                <ul className="list-disc list-inside space-y-2 text-orange-800">
                  <li>asimetri (yüzün bir tarafında fazla tutulum),</li>
                  <li>şişlik (ödemle karışan hacim artışı),</li>
                  <li>alerjik kızarıklık veya sert nodül ile sonuçlanabilir.</li>
                </ul>
                <p className="text-orange-800 mt-4">
                  Klinik olarak kalıcı hasar nadir görülse de, bebeğe etkisi bilinmediği için etik olarak uygulanmaz.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Uzman Görüşü
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Gebelikte dolgu uygulamak, tıbben gerekçelendirilemez.
                  Çünkü elimizde hem yeterli güvenlik verisi yok, hem de hormonel değişimlerin etkisiyle sonuçlar öngörülemez hale gelir.
                  En doğru yaklaşım, doğum sonrası dönemde cilt dengesi oturduğunda işlem yapmaktır."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Psikolojik Boyut
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Hamilelikte aynadaki değişiklikler birçok kadında "ben eskisi gibi değilim" duygusunu tetikler.
                Bu noktada estetik müdahaleler, fizikselden çok duygusal bir ihtiyaç haline gelebilir.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Ancak bu süreçte kendini suçlamadan, sabırla beklemek, doğum sonrası dönemde çok daha sağlıklı sonuçlar alınmasını sağlar.
              </p>

              <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
                <p className="text-green-800 font-medium">
                  💬 Cilt, doğumdan sonra hormonlarla birlikte yeniden dengeye gelir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. Doğal ve Güvenli Alternatifler
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-indigo-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Alternatif</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">C vitamini serumları</td>
                      <td className="border border-gray-300 px-4 py-3">Kolajen sentezini destekler, cildi aydınlatır.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hyaluronik asitli nemlendiriciler (topikal)</td>
                      <td className="border border-gray-300 px-4 py-3">Ciltte geçici dolgunluk sağlar, sistemik etkisi yoktur.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Yüz egzersizleri</td>
                      <td className="border border-gray-300 px-4 py-3">Kas tonusunu korur, doğal lifting etkisi verir.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">PRP ve vitamin kokteylleri</td>
                      <td className="border border-gray-300 px-4 py-3">Doğum sonrası döneme ertelenebilir.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8. Doğum Sonrası Dolgu Uygulaması
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Doğum sonrası emzirme süreci tamamlandıktan ve hormonal denge oturduktan sonra dolgu işlemi güvenle yapılabilir.
                Bu genellikle doğumdan 6 ay sonra önerilir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Dolgu sonuçları, gebelik öncesine göre daha kalıcı olur çünkü cilt yenilenme hızı normale döner.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Hamileyken dolgu yaptırmak kesinlikle yasak mı?</h3>
                  <p className="text-gray-600">Yasal olarak yasak değildir ama tüm uluslararası kılavuzlarda önerilmez.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Hamile olduğumu dolgu sonrası öğrendim, zararlı mı?</h3>
                  <p className="text-gray-600">Genelde ciddi risk bildirilmemiştir; ancak doktor takibi gerekir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Hamilelikte dudak dolgusu yapılabilir mi?</h3>
                  <p className="text-gray-600">Hayır. Dudak bölgesi kanlanması yüksek olduğu için risklidir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Alternatif olarak ne yapılabilir?</h3>
                  <p className="text-gray-600">Topikal hyaluronik asitli bakım ürünleri kullanılabilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sonuç
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Hamilelikte dolgu yaptırmak tıbben gereksiz ve potansiyel olarak risklidir.
                Bu dönemde vücudun doğal değişimlerine saygı göstermek, hem anne hem bebek sağlığı açısından en doğru karardır.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Doğum sonrası dönem, estetik işlemler için çok daha güvenli ve kalıcı sonuçlar sunar.
                Unutmayın: hamilelikte en güzel dolgu, annenin yüzündeki mutluluktur.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>FDA (2024) – "Dermal Filler Safety and Pregnancy Statement"</li>
                  <li>ISAPS Global Report (2024) – "Injectables in Pregnant and Lactating Women"</li>
                  <li>Harvard Health Publishing (2024) – "Aesthetic Procedures During Pregnancy"</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Gebelikte Estetik Müdahaleler Rehberi"</li>
                  <li>American Society for Dermatologic Surgery (ASDS) – "Guidelines on Fillers During Pregnancy"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#dolgu</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#hamilelik</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#gebelik</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#anneadayı</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#güvenlik</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-rose-600 to-pink-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Hamilelik Döneminde Güvenli Estetik Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan hamilelik döneminde güvenli estetik seçenekleri hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Hamilelik döneminde estetik seçenekleri hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-rose-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/hamilelikte-botoks-bilimsel-gercekler-riskler-ve-uzman-gorusu"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Hamilelikte Botoks</h4>
              <p className="text-gray-600 text-sm">Hamilelikte botoksun güvenliği, riskleri ve uzman önerileri hakkında detaylı bilgiler.</p>
            </a>
            <a
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir, Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoksun ne olduğu, nasıl çalıştığı ve uygulama süreci hakkında detaylı bilgiler.</p>
            </a>
            <a
              href="/tr/blog/orijinal-dolgu-anlama-yontemleri-sahte-urunlerden-korunma"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Orijinal Dolguyu Anlama Yöntemleri</h4>
              <p className="text-gray-600 text-sm">Orijinal dolgu nasıl anlaşılır ve sahte ürünlerden nasıl korunulur?</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-rose-600 text-white rounded-full hover:bg-rose-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
